import type { Locale } from '@shared/models/auth';
import { DateFormat } from './types';

type OptKey = keyof Intl.DateTimeFormatOptions;
type OptValue = Intl.DateTimeFormatOptions[OptKey];

type FormatPart =
  | 'EEEE'
  | 'EEE'
  | 'E'
  | 'yyyy'
  | 'MM'
  | 'MMM'
  | 'MMMM'
  | 'dd'
  | 'HH'
  | 'mm'
  | 'ss'
  | 'SSS';

/** A map of standard format parts to native Intl.DateTimeFormatOptions keys
 * For example a format string of `yyyy-MM-dd` would be translated to
 * ```
 * {
 *    year: 'numeric',
 *    month: '2-digit',
 *    day: '2-digit',
 * }
 * ```
 * */
const intlMap: Record<FormatPart, OptKey> = {
  yyyy: 'year',
  MM: 'month',
  MMM: 'month',
  MMMM: 'month',
  EEEE: 'weekday',
  EEE: 'weekday',
  E: 'weekday',
  dd: 'day',
  HH: 'hour',
  mm: 'minute',
  ss: 'second',
  SSS: 'fractionalSecondDigits',
};

/** A map of functions that assign value to `Intl.DateTimeFormatOptions` key based on the `FormatPart` */
const optionMapper: Partial<Record<OptKey, (arg: FormatPart) => OptValue>> = {
  year: () => 'numeric',
  month: (m) => {
    switch (m) {
      case 'MMMM':
        return 'long';
      case 'MMM':
        return 'short';
      case 'MM':
        return '2-digit';
      default:
        throw new Error(`${m} format is not defined`);
    }
  },
  weekday: (w) => {
    switch (w) {
      case 'EEEE':
        return 'long';
      case 'EEE':
        return 'short';
      case 'E':
        return 'narrow';
      default:
        throw new Error(`${w} format is not defined`);
    }
  },
  day: () => '2-digit',
  hour: () => '2-digit',
  minute: () => '2-digit',
  second: () => '2-digit',
  fractionalSecondDigits: () => 3,
};

/** Creates a configuration object (`Intl.DateTimeFormatOptions`) based on the `FormatPart`s*/
const intlOptionMapper = (parts: FormatPart[], useUTC = false) => {
  const initialOpts: Intl.DateTimeFormatOptions = { hour12: false };
  if (useUTC) {
    initialOpts.timeZone = 'UTC';
  }

  return parts.reduce((opts, part) => {
    const key = intlMap[part];
    if (optionMapper[key]) {
      const value = optionMapper[key](part);
      Object.assign(opts, { [key]: value });
      return opts;
    }
    return opts;
  }, initialOpts);
};

const formatConfig = {
  format: DateFormat.ISO as DateFormat,
  locale: 'de-DE' as Locale,
  /** Ignores custom separators and uses native ones instead  based on locale */
  useNativeLiterals: true,
  /** Uses UTC timezone for formatting */
  useUTC: false,
};

type DefaultFormatConfig = typeof formatConfig;

// Only format is required, other props are optional
type FormatDateConfig = Pick<DefaultFormatConfig, 'format'> &
  Partial<Omit<DefaultFormatConfig, 'format'>>;
export const formatDate = (
  date: string | Date,
  config: FormatDateConfig = formatConfig,
) => {
  if (!date) {
    return '';
  }
  try {
    const updConfig = { ...formatConfig, ...config };
    const parts = updConfig.format.split(/[^\w]/) as FormatPart[];
    if (updConfig.useNativeLiterals) {
      return Intl.DateTimeFormat(
        updConfig.locale,
        intlOptionMapper(parts, updConfig.useUTC),
      ).format(new Date(date));
    }
    const fmtParts = Intl.DateTimeFormat(
      updConfig.locale,
      intlOptionMapper(parts, updConfig.useUTC),
    )
      .formatToParts(new Date(date))
      .filter((part) => part.type !== 'literal')
      .reduce(
        (rec, part) => {
          rec[part.type as OptKey | 'fractionalSecond'] = part.value;
          return rec;
        },
        {} as Record<OptKey | 'fractionalSecond', string>,
      );

    return parts.reduce(
      (str, part) =>
        str.replace(
          part,
          fmtParts[
            part === 'SSS' ? ('fractionalSecond' as OptKey) : intlMap[part]
          ],
        ),
      updConfig.format as string,
    );
  } catch (_) {
    return '';
  }
};
